import saveAs from "file-saver";
import config from "../config.json";
import { IPageable, Params } from "../types/table/interfaces.type";
import { createAxiosInstance } from "./ServicesHelpers";
import { useQuery, UseQueryResult } from "@tanstack/react-query";

const axiosInstance = createAxiosInstance(config.ADMIN_API_BASE_URL);

export interface EmailTemplateType {
  id: string;
  templateName: string;
  templateDescription: string;
  templateBody: string;
  eventType: string;
  templateSubject: string;
  createdDate: Date;
  historyTimestamp?: Date;
  fromEmail?: string;
  personal?: string;
  smtpHost?: string;
  smtpPort?: string | null;
  smtpUsername?: string;
  smtpPassword?: string;
  useTls?: boolean;
  createdBy?: string;
  updatedBy?: string;
  useSsl?: boolean;
}

export interface EmailTemplateHistory {
  id: string;
  snapshot: EmailTemplateType & { timestamp: string };
  historyTimestamp: string;
  mainId: number;
  createdBy: string;
}

export interface EmailTemplateHistoryDiff {
  historyTimestamp: string;
  mainId: number;
  createdBy: string;
  snapshot: EmailTemplateType & { timestamp: string };
  diff?: Record<string, string>;
}

export enum TemplateSubject {
  ACCOUNT_PROFILE_NAME,
  ACCOUNT_USER_NAME,
  DATE,
}
export enum TemplateBodyPlaceholder {
  ACCOUNT_PROFILE_NAME,
  ACCOUNT_USER_NAME,
  ALERT_PERCENTAGE,
  DATE,
  USERNAME,
  USEREMAIL,
  PASSWORD,
  CLIENT_BALANCE,
  CURRENCY,
  CLIENT_BALANCE_LIMIT,
  LOGS,
  AZSHEET_TABLE,
  SMPP_USERNAME
}

export const exportEmailTemplate = async (
  params: Params<EmailTemplateType> = {}
): Promise<void> => {
  try {
    const response = await axiosInstance.get("/emails/csv", {
      params,
      responseType: "blob",
    });

    const disposition = response.headers["content-disposition"];
    let filename = "EmailTemplate.csv";
    if (disposition) {
      const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (match && match[1]) {
        filename = match[1].replace(/['"]/g, "").trim();
      }
    }

    const blob = new Blob([response.data], { type: "text/csv" });
    saveAs(blob, filename);

    console.log("Export successful and file downloaded.");
  } catch (error: any) {
    console.error(
      "Error fetching EmailTemplate:",
      error.response?.data?.message || error.message || error
    );
  }
};

export async function fetchEmailTemplates(
  params: Params<EmailTemplateType>
): Promise<IPageable<EmailTemplateType>> {
  try {
    const response = await axiosInstance.get("/emails", { params });
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while getting the EmailTemplates."
    );
  }
}

export async function fetchEmailTemplate(
  id: string
): Promise<EmailTemplateType> {
  try {
    const response = await axiosInstance.get("/emails/" + id);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while getting the EmailTemplate."
    );
  }
}

export const createEmailTemplate = async (
  EmailTemplateData: Partial<EmailTemplateType>
) => {
  try {
    const response = await axiosInstance.post("/emails", EmailTemplateData);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while Adding the EmailTemplate."
    );
  }
};

export const updateEmailTemplate = async (
  EmailTemplateData: Partial<EmailTemplateType>
) => {
  try {
    const response = await axiosInstance.put(
      `/emails/${EmailTemplateData.id}`,
      EmailTemplateData
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while updating the EmailTemplate."
    );
  }
};

export const useEmailTemplatesQuery = (
  params: Params<EmailTemplateType>
): UseQueryResult<IPageable<EmailTemplateType>, Error> => {
  const queryKey = ["emailTemplate", JSON.stringify(params)];
  return useQuery({
    queryKey,
    queryFn: () => fetchEmailTemplates(params),
  });
};



export async function fetchEmailTemplateHistory(
  id: EmailTemplateType["id"],
  params: Params<EmailTemplateHistory> = {}
): Promise<IPageable<EmailTemplateType>> {
  try {
    const response = await axiosInstance.get(`/emails/history`,{ params: { mainId :id , sort: "historyTimestamp,desc", ...params } });
    const data = response.data;
    return {
      ...data,
      content: data.content.map((item: any) => ({
        ...item.snapshot,
        historyTimestamp: item.historyTimestamp,
      })),
    };
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        `An error occurred while getting the Email Template history for ID ${id}.`
    );
  }
}